import { Player } from "@keepoinc/site-page-react-component";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { CircleSpinner } from "../../../../../../components/CircleSpinner";
import Input from "../../../../../../components/Form/Input/input";
import Label from "../../../../../../components/Form/Label";
import { Form, FormGroup } from "../../../../../../components/Form/styles";
import { Box } from "../../../../../../components/Layout";
import SaveBar from "../../../../../../components/SaveBar";
import { SectionHelp } from "../../../../../../components/SectionHelp";
import { SubHeader } from "../../../../../../components/SubHeader";
import { MediaSection } from "../../../../../../model/site";
import { normalizeMediaLink, shortLinkProvider } from "../../../../../../utils/mediaLink";
import { useSection } from "../../../../hooks/Section";
import { AudioIcon } from "../../../Sections/icons/Audio";
import { VideoIcon } from "../../../Sections/icons/Video";
import { Container, PlayerContainer, PreviewContainer } from "./styles";

// Spotify has a native compact player at 80px; other players keep the height they need, otherwise
// their content scrolls inside the frame
const compactHeightOf = (link?: string) => (link && /open\.spotify\.com\//i.test(link) ? "80" : undefined);

// The preview waits for a pause in the typing before loading a player, so it doesn't flicker
const TYPING_PAUSE = 1000;

type Props = {
  helpMessage: string;
  category: "AUDIO" | "VIDEO";
  headerPageTitle: string;
};

export const EmbeddedMediaPage = ({ helpMessage, category, headerPageTitle }: Props) => {
  const { t } = useTranslation();

  const { section, setSection, saveSection, hasUnsavedChanges } = useSection<MediaSection>("media");

  const [hasErrorLink, setHasErrorLink] = useState(false);
  const [isLoading, setLoading] = useState(false);

  // The field has its own value; the section (what the live preview beside the editor renders, and
  // what's saved) only gets a link once the preview here accepts it. The live preview's player doesn't
  // filter by category, so an unchecked link (e.g. YouTube in an audio section) would play there.
  const [linkInput, setLinkInput] = useState(section.link || "");
  const [previewLink, setPreviewLink] = useState(normalizeMediaLink(section.link));

  const setSectionLink = useCallback(
    (link?: string) => setSection(current => (current.link === link ? current : { ...current, link })),
    [setSection]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      // Once the typing pauses (or right after a paste), the field takes the cleaned-up link
      const normalized = normalizeMediaLink(linkInput) || "";
      if (normalized !== linkInput) {
        setLinkInput(normalized);
      }
      setPreviewLink(normalized);
      // Nothing to validate: the section drops its link right away
      if (!normalized || shortLinkProvider(normalized)) {
        setSectionLink(undefined);
      }
    }, TYPING_PAUSE);
    return () => clearTimeout(timer);
  }, [linkInput, setSectionLink]);
  const isTyping = previewLink !== linkInput;

  // Everything the preview shows follows the settled link, not each keystroke
  const playerLink = previewLink;
  const shortLinkOf = shortLinkProvider(previewLink);
  const showLinkError = !isTyping && hasErrorLink && !!previewLink;

  // The player reports a supported link by starting to load it: that's when the section takes it
  const playerLinkRef = useRef(playerLink);
  playerLinkRef.current = playerLink;
  const onUpdateLoading = useCallback(
    (loading: boolean) => {
      setLoading(loading);
      if (loading) {
        setSectionLink(playerLinkRef.current);
      }
    },
    [setSectionLink]
  );

  const onProviderNotFound = useCallback(() => {
    setHasErrorLink(true);
    setLoading(false);
    setSectionLink(undefined);
  }, [setSectionLink]);

  const onError = useCallback(() => {
    setHasErrorLink(true);
    setLoading(false);
    setSectionLink(undefined);
  }, [setSectionLink]);

  // The package writes the provider's own height on the iframe (Spotify 380): for a compact player,
  // rewrite it whenever the player (re)renders.
  const playerContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const container = playerContainerRef.current;
    const compactHeight = compactHeightOf(playerLink);
    if (!container || !compactHeight) {
      return;
    }
    const applyHeight = () =>
      container.querySelectorAll("iframe").forEach(iframe => {
        if (iframe.getAttribute("height") !== compactHeight) {
          iframe.setAttribute("height", compactHeight);
        }
      });
    applyHeight();
    const observer = new MutationObserver(applyHeight);
    observer.observe(container, { childList: true, subtree: true, attributes: true, attributeFilter: ["height"] });
    return () => observer.disconnect();
  });

  const saveHandler = useCallback(() => {
    saveSection({ ...section, subType: category.toLowerCase() });
  }, [section, saveSection, category]);

  const updateLinkHandler = (value: string) => {
    setHasErrorLink(false);
    setLinkInput(value);
  };

  return (
    <Container>
      <SubHeader
        title={headerPageTitle}
        crumbs={[{ label: t("pages.editor.preview.footer.actions.sections"), to: "/editor/sections" }]}
        hasUnsavedChanges={hasUnsavedChanges}
      />
      <SectionHelp message={helpMessage} />
      <Form>
        <Box borderBottom>
          <FormGroup>
            <Input
              type="text"
              id="title"
              value={section.pageTitle}
              onChange={e =>
                setSection({
                  ...section,
                  pageTitle: e.target.value,
                })
              }
              label={t("pages.editor.media.embeddedMedia.form.title")}
            />
          </FormGroup>
          <FormGroup>
            <Input
              type="text"
              id="link"
              value={linkInput}
              required
              onChange={e => updateLinkHandler(e.target.value)}
              label={t("pages.editor.media.embeddedMedia.form.url.label")}
              infoMessage={
                !isTyping && shortLinkOf
                  ? t("pages.editor.media.embeddedMedia.form.url.shortLink", { provider: shortLinkOf })
                  : showLinkError
                    ? t("pages.editor.media.embeddedMedia.form.url.error")
                    : undefined
              }
            />
          </FormGroup>
          <FormGroup>
            {playerLink && !shortLinkOf && (
              <PlayerContainer
                ref={playerContainerRef}
                hide={isTyping || isLoading || hasErrorLink}
                $compactHeight={compactHeightOf(playerLink)}>
                <Player
                  link={playerLink}
                  category={category}
                  onUpdateLoading={onUpdateLoading}
                  onError={onError}
                  onProviderNotFound={onProviderNotFound}
                />
              </PlayerContainer>
            )}
            {(!playerLink || shortLinkOf || isLoading || hasErrorLink || isTyping) && (
              <PreviewContainer>
                {isLoading && !isTyping ? (
                  <CircleSpinner size="40px" />
                ) : category === "VIDEO" ? (
                  <VideoIcon color="var(--color-neutral-base)" width={67} height={56} />
                ) : (
                  <AudioIcon color="var(--color-neutral-base)" width={67} height={56} />
                )}
              </PreviewContainer>
            )}
            <Label text="Preview" htmlFor="" />
          </FormGroup>
        </Box>
      </Form>
      <SaveBar
        sticky
        onSave={saveHandler}
        disabled={!section.link || isTyping || isLoading || hasErrorLink || !!shortLinkOf}
        hasUnsavedChanges={hasUnsavedChanges}
      />
    </Container>
  );
};
